'use strict';

var extend = require('extend');
var limitNumber = require('../lib/limitNumber.js');
var Listener = require('../lib/core/Listener.js');
var EventListenerOption = require('../lib/EventListenerOption.js');

var IS_SETUP_CLASS_NAME = 'isSetup';

var IS_OPEN_CLASS_NAME = 'isOpen';


// Options defaults
var defaults = {
	isOnlyOneOpen : true,
	selectors : {
		item : ':scope > .accordion__item',
		body : ':scope > .accordion__body',
		content : ':scope > .accordion__body > .accordion__content',
		control : ':scope > .accordion__heading'
	},
	openTransition : {
		speed : 1.5,
		minDuration : 200,
		maxDuration : 400
	},
	closeTransition : null, // Defaults to same as openTransition
	listeners : null
};

/**
 * @class
 * @classdesc Accordion class which can be used to simple and advanced accordions quickly.
 * 
 * This class supports listeners (on, off, etc.) via the Listener class.
 * 
 * Listeners this class triggers are ('this' references the instance of Accordion):
 * <ul>
 * <li>setup
 * <li>beforeOpen - Data object passed to event listener contains a 'itemElement' property which is a reference to the item that is being opened.
 * <li>afterOpen - Data object passed to event listener contains a 'itemElement' property.
 * <li>beforeClose - Data object passed to event listener contains a 'itemElement' property.
 * <li>afterClose - Data object passed to event listener contains a 'itemElement' property.
 * </ul>
 * 
 * @param {Element} accordionElement - Main level accordion object. This typically would have the 'accordion' class name.  
 * @param {object} [options] - With the following configuration properties ...
 * @param {boolean} [options.isOnlyOneOpen=true]
 * @param {object} [options.selectors] - Object of selectors.
 * @param {string} [options.selectors.item='> .accordion__item'] - Queried with context of accordionElement.
 * @param {string} [options.selectors.body='> .accordion__body'] - Queried with context of item.
 * @param {string} [options.selectors.content='> .accordion__body > .accordion__content'] - Queried with context of item.
 * @param {string} [options.selectors.control='> .accordion__heading'] - Queried with context of item.
 * @param {object} [options.openTransition]
 * @param {number} [options.openTransition.speed=1.5] - Speed of transition in px per ms (e.g. 100 would mean transition 100px in 1ms). If you do not want the duration to be dynamically calculated based on change in height, set this to null.
 * @param {number} [options.openTransition.minDuration=200]
 * @param {number} [options.openTransition.maxDuration=750]
 * @param {object} [options.closeTransition] - Defaults to the same as 'options.openTransition' using a deep extend so you can override one value or all properties of 'options.openTransition'.
 * @param {number} [options.closeTransition.speed]
 * @param {number} [options.closeTransition.minDuration]
 * @param {number} [options.closeTransition.maxDuration]
 */
function Accordion(accordionElement, options) {
	var opts = this._options = extend(true, {}, defaults, options);
	
	opts.closeTransition = extend(true, {}, opts.openTransition, opts.closeTransition);

	// Store references
	this._elements = {
		accordion : accordionElement,
		items : accordionElement.querySelectorAll(opts.selectors.item)
	};
	
	// Adds a few basic listener methods (on, off and trigger) to this initiated object.
	Listener.init(this);
	
	// Setup any listeners that were specified in options
	this.onEach(opts.listeners);
	
	// Setup click listeners
	for (var i=0, item, heading; i < this._elements.items.length; i++) {
		item = this._elements.items[i];
		heading = item.querySelectorAll(opts.selectors.control)[0];
		heading.addEventListener('click', _onItemClick.bind(this, item), false);
	}
	
	// Close all
	this.closeAll();
	
	// Setup window resize listener
	window.addEventListener('resize', _onWindowResize.bind(this), EventListenerOption.get());
	
	// Mark as setup
	accordionElement.classList.add(IS_SETUP_CLASS_NAME);
	this._isSetup = true;
	this.trigger('setup'); // This allows you to do work when Accordions are setup dynamically (aka you don't know they are being setup).
	
	// TODO Need some sort of destroy to clean up references. Or do we need some sort of weak reference..? Really if we could self destroy when we detect the accordion element becomes detached that may be good.. but how? is that possible?
}

Accordion.COMPONENT_STYLE_CLASS = 'accordion';

/**
 * @private
 */
function _onItemClick(item, event) {
	this.toggle(item);
}

/**
 * @private
 */
function _onWindowResize(event) {
	for (var i=0, item; i < this._elements.items.length; i++) {
		item = this._elements.items[i];
		if (this.isOpen(item)) {
			this._setHeightOnItem(item);
		}
	}
}

/**
 * @private
 */
Accordion.prototype._setHeightOnItem = function(itemElement, val) {
	var body = itemElement.querySelectorAll(this._options.selectors.body)[0];
	var currentHeight = parseInt(window.getComputedStyle(body).height);

	// Update height
	var newHeight = val !== undefined ?
		0 : itemElement.querySelectorAll(this._options.selectors.content)[0].scrollHeight
	body.style.height = newHeight + 'px';
	
	// Set speed of transition (if support by browser, otherwise it will fallback to what is in the CSS)
	var transitionOption = (newHeight > currentHeight) ? this._options.openTransition : this._options.closeTransition;
	if (transitionOption.speed !== null && body.style.transitionDuration !== undefined) {
		var duration = 0;
		
		if (this._isSetup === true) {
			var heightDelta = Math.abs(newHeight - currentHeight);
			if (heightDelta !== 0) {
				duration = limitNumber(heightDelta * transitionOption.speed, transitionOption.minDuration, transitionOption.maxDuration);
			}
		}
		
		body.style.transitionDuration = duration + 'ms';
	}
}

/**
 * @public
 */
Accordion.prototype.setOnlyOneOpen = function(isOnlyOneOpen) {
	this._options.isOnlyOneOpen = isOnlyOneOpen;
}

/**
 * @public
 */
Accordion.prototype.isOnlyOneOpen = function() {
	return this._options.isOnlyOneOpen;
}

/**
 * @public
 * @returns {boolean} Returns true of open is successful. Open can be prevented via 'beforeOpen' event.
 */
Accordion.prototype.open = function(itemElement) {
	var eventData = {itemElement : itemElement};
	if (this.trigger('beforeOpen', eventData).defaultPrevented) { return false; }
	
	for (var i=0, item; i < this._elements.items.length; i++) {
		item = this._elements.items[i];
		if (item === itemElement) {
			itemElement.classList.add(IS_OPEN_CLASS_NAME);
			this._setHeightOnItem(itemElement);
		} else if (this.isOnlyOneOpen() === true) {
			this.close(item);
		}
	}
	
	this.trigger('afterOpen', eventData);
	return true;
}

/**
 * @public
 * @returns {boolean} Returns true of open is successful. Open can be prevented via 'beforeClose' event.
 */
Accordion.prototype.close = function(itemElement) {
	var eventData = {itemElement : itemElement};
	if (this.trigger('beforeClose', eventData).defaultPrevented) { return false; }
	
	itemElement.classList.remove(IS_OPEN_CLASS_NAME);
	this._setHeightOnItem(itemElement, 0);
	
	this.trigger('afterClose', eventData);
	return true;
}

/**
 * @public
 */
Accordion.prototype.closeAll = function(itemElement) {
	for (var i=0, item; i < this._elements.items.length; i++) {
		item = this._elements.items[i];
		this.close(item);
	}
}

/**
 * @public
 */
Accordion.prototype.isOpen = function(itemElement) {
	return itemElement.classList.contains(IS_OPEN_CLASS_NAME);
}

/**
 * @public
 */
Accordion.prototype.toggle = function(itemElement) {
	if (this.isOpen(itemElement)) {
		this.close(itemElement);
	} else {
		this.open(itemElement);
	}
}


module.exports = Accordion;

